import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { X, Link2 } from "lucide-react";
import { Link } from "react-router-dom";
import HelpTip from "@/components/HelpTip";
import { BILL_CATEGORIES, RECURRENCE } from "@/lib/constants";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function BillModal({ open, onClose, onSaved, bill }) {
  const empty = {
    name: "", amount: "", minimum_payment: "", due_date: new Date().toISOString().slice(0, 10),
    interest_rate: 0, category: "utilities", recurrence: "monthly", autopay: false, notes: "",
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (bill) setForm({ ...bill });
    else setForm(empty);
  }, [bill, open]);

  if (!open) return null;

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const linked = !!bill?.debt_id;
  const ro = linked ? " opacity-60 cursor-not-allowed" : "";

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount) || 0,
        minimum_payment: parseFloat(form.minimum_payment) || parseFloat(form.amount) || 0,
        interest_rate: parseFloat(form.interest_rate) || 0,
        autopay: !!form.autopay,
      };
      if (bill?.id) {
        await api.put(`/bills/${bill.id}`, payload);
        toast.success("Bill updated");
      } else {
        await api.post(`/bills`, payload);
        toast.success("Bill added");
      }
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={save} data-testid="bill-modal"
        className="w-full max-w-lg bg-card border border-border rounded-2xl p-6 shadow-2xl fade-up my-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display text-xl font-bold">{bill ? "Edit Bill" : "Add Bill"}</h3>
          <button type="button" onClick={onClose} className="p-1 rounded-lg hover:bg-accent"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {linked && (
            <div className="col-span-2 flex items-start gap-2 px-3 py-2.5 rounded-lg border border-indigo-500/30 bg-indigo-500/10 text-[11px] text-indigo-300" data-testid="bill-linked-debt-note">
              <Link2 className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>This bill mirrors a debt's minimum payment. Change the name, amount, APR or due date from the <Link to="/debts" className="underline font-semibold">Debts page</Link>. Only autopay and notes are editable here.</span>
            </div>
          )}
          <Field label="Name" full>
            <input required disabled={linked} data-testid="bill-name-input" value={form.name} onChange={e => set("name", e.target.value)} className={inp + ro} placeholder="Electric bill" />
          </Field>
          <Field label="Amount">
            <input required disabled={linked} type="number" step="0.01" data-testid="bill-amount-input" value={form.amount} onChange={e => set("amount", e.target.value)} className={inp + ro} placeholder="128.50" />
          </Field>
          <Field label="Min Payment">
            <input disabled={linked} type="number" step="0.01" value={form.minimum_payment} onChange={e => set("minimum_payment", e.target.value)} className={inp + ro} placeholder="128.50" />
          </Field>
          <Field label="Due Date">
            <input required disabled={linked} type="date" data-testid="bill-due-input" value={form.due_date} onChange={e => set("due_date", e.target.value)} className={inp + ro} />
          </Field>
          <Field label="Interest %">
            <input disabled={linked} type="number" step="0.1" value={form.interest_rate} onChange={e => set("interest_rate", e.target.value)} className={inp + ro} />
          </Field>
          <Field label="Category">
            <select disabled={linked} value={form.category} onChange={e => set("category", e.target.value)} className={inp + ro} data-testid="bill-category-select">
              {BILL_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </Field>
          <Field label={<>Recurrence <HelpTip k="recurrence" /></>}>
            <select disabled={linked} value={form.recurrence} onChange={e => set("recurrence", e.target.value)} className={inp + ro} data-testid="bill-recurrence-select">
              {RECURRENCE.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </Field>
          <Field label="Notes" full>
            <input value={form.notes || ""} onChange={e => set("notes", e.target.value)} className={inp} placeholder="Optional" />
          </Field>
          <label className="col-span-2 flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border border-border bg-background/50 cursor-pointer hover:bg-accent/40 transition">
            <div>
              <div className="text-sm font-semibold flex items-center gap-2">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4 text-teal-500"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                Autopay enabled <HelpTip k="autopay" />
              </div>
              <div className="text-[11px] text-muted-foreground">Hides this bill from upcoming reminders and marks the calendar tag with a lock icon.</div>
            </div>
            <input type="checkbox" data-testid="bill-autopay-toggle"
              checked={!!form.autopay} onChange={e => set("autopay", e.target.checked)}
              className="h-5 w-5 accent-teal-500 cursor-pointer" />
          </label>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg border border-border hover:bg-accent transition text-sm">Cancel</button>
          <button disabled={saving} type="submit" data-testid="bill-save-btn"
            className="px-5 py-2 rounded-lg bg-teal-500 text-white font-semibold hover:bg-teal-600 transition text-sm disabled:opacity-60">
            {saving ? "Saving…" : "Save Bill"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}

const inp = "w-full px-3 py-2 rounded-lg bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:border-teal-500 transition";
function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "col-span-2" : ""}`}>
      <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
